import { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Copy, 
  Check, 
  ExternalLink,
  Calendar,
  Clock,
  Sparkles,
  Users,
  Globe,
  TrendingUp,
  Hash,
  AtSign,
  Link as LinkIcon,
  Image,
  Video,
  FileText,
  Coins
} from 'lucide-react';
import { toast } from 'sonner';

// Social Icons
const InstagramIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
);

const TikTokIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const XIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const YouTubeIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

export default function SocialMediaHub() {
  const [copiedId, setCopiedId] = useState(null);
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://susumoney.com';

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      toast.success('Copied!');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  // Brand Kit Data
  const brandKit = {
    usernames: {
      primary: '@globalsusumoneys',
      alternatives: ['@susumoney', '@susumoneyapp', '@susumoney_official', '@getsusumoney']
    },
    bios: {
      instagram: `💰 Save Together, Grow Together
🌍 Global Community Savings Platform
✨ Start with just $0.10
👇 Join FREE - Link below!
#Susu #CommunitySavings`,
      tiktok: `💰 Susu Money - Save Together!
🌍 5,000+ savers in 50+ countries
✨ Start with $0.10
🔗 Link in bio to join FREE`,
      facebook: `Susu Money - Global Community Savings Platform 🌍💰

Join 5,000+ savers worldwide building wealth through traditional community savings, reimagined for the digital age.

✨ Start saving with as little as $0.10
🔒 100% Secure with Stripe
📈 Earn interest on your savings
🤝 Save with friends & family globally

Join FREE today! 👇`,
      twitter: `💰 Save Together, Grow Together | Global Community Savings Platform | 🌍 50+ Countries | Start with $0.10 | Join FREE 👇`,
      youtube: `Welcome to Susu Money! 🌍💰

We're revolutionizing the traditional African savings circle (Susu/Esusu/Tontine) for the digital age.

🔥 What we offer:
• Save with as little as $0.10
• Join savings clubs worldwide  
• Earn interest on your savings
• 100% secure with Stripe
• Available in 50+ countries

Subscribe for:
📚 Financial education
💡 Savings tips
🎉 Success stories
🌍 Community highlights

Join our community: ${baseUrl}

#SusuMoney #CommunitySavings #FinancialFreedom`
    },
    hashtags: {
      primary: ['#SusuMoney', '#CommunitySavings', '#SaveTogether', '#FinancialFreedom', '#AfricanFintech'],
      trending: ['#MoneyTok', '#FinTok', '#SavingsChallenge', '#MoneyGoals', '#GenZFinance'],
      niche: ['#Esusu', '#Tontine', '#AfricanDiaspora', '#RemittanceAlternative', '#GroupSavings']
    }
  };

  // Content Calendar - First 10 Posts
  const contentCalendar = [
    {
      day: 'Monday',
      week: 1,
      type: 'image',
      platform: 'all',
      title: 'Introduction Post',
      caption: `💰 Introducing Susu Money!

The traditional African savings circle, reimagined for the digital age. 🌍

✨ Save with friends & family worldwide
✨ Start with just $0.10
✨ 100% secure

Join 5,000+ savers today! Link in bio 👆

#SusuMoney #CommunitySavings #FinancialFreedom`,
      hashtags: '#SusuMoney #CommunitySavings #SaveTogether',
      bestTime: '6:00 PM'
    },
    {
      day: 'Wednesday',
      week: 1,
      type: 'video',
      platform: 'tiktok/reels',
      title: 'What is Susu?',
      caption: `Ever heard of Susu? 🤔

It's a traditional African savings method where groups save together and take turns receiving the pool! 

Now it's digital and GLOBAL! 🌍💰

Join FREE: Link in bio

#WhatIsSusu #AfricanTradition #MoneyTok #SavingsHack`,
      hashtags: '#MoneyTok #FinTok #SavingsHack',
      bestTime: '7:00 PM'
    },
    {
      day: 'Friday',
      week: 1,
      type: 'carousel',
      platform: 'instagram',
      title: '5 Reasons to Join',
      caption: `5 Reasons to Join Susu Money 🚀

1️⃣ Start with just $0.10
2️⃣ Save with friends globally
3️⃣ Earn interest on savings
4️⃣ 100% secure (Stripe powered)
5️⃣ FREE to join!

Which reason speaks to you? 👇

#SusuMoney #SavingsGoals #FinancialLiteracy`,
      hashtags: '#SavingsGoals #MoneyTips #FinancialLiteracy',
      bestTime: '12:00 PM'
    },
    {
      day: 'Sunday',
      week: 1,
      type: 'story',
      platform: 'instagram/tiktok',
      title: 'Behind the Scenes',
      caption: `POV: You just joined your first Susu club 🎉

That feeling when your savings finally have a community! 💰

Join us: Link in bio

#SusuMoney #SavingsJourney`,
      hashtags: '#POV #SavingsJourney',
      bestTime: '8:00 PM'
    },
    {
      day: 'Tuesday',
      week: 2,
      type: 'image',
      platform: 'all',
      title: 'Milestone Celebration',
      caption: `🎉 WE HIT 5,000 SAVERS! 

Thank you to our amazing global community! 🌍

From Ghana to USA, UK to Nigeria, Australia to Canada - you're all part of this journey!

Who's joining us on the road to 10K? 🚀

#SusuMoney #Milestone #CommunityLove`,
      hashtags: '#Milestone #ThankYou #Community',
      bestTime: '6:00 PM'
    },
    {
      day: 'Thursday',
      week: 2,
      type: 'video',
      platform: 'tiktok/reels',
      title: 'How It Works',
      caption: `How Susu Money Works in 30 seconds ⏱️

1. Sign up FREE
2. Join or create a club
3. Make small contributions
4. Watch your savings grow!

It's that simple! 💰

#HowItWorks #SavingsApp #MoneyTok`,
      hashtags: '#Tutorial #HowTo #MoneyTok',
      bestTime: '7:00 PM'
    },
    {
      day: 'Saturday',
      week: 2,
      type: 'meme',
      platform: 'all',
      title: 'Relatable Meme',
      caption: `Me: "I need to start saving"
Also me: *joins Susu Money*

Now I save with 5,000+ people worldwide 😎💰

Your turn! Link in bio 👆

#SavingsMeme #Relatable #SusuMoney`,
      hashtags: '#Meme #Relatable #MoneyMemes',
      bestTime: '11:00 AM'
    },
    {
      day: 'Monday',
      week: 3,
      type: 'testimonial',
      platform: 'all',
      title: 'Success Story',
      caption: `"I saved $500 in 3 months with my Susu club!" 💰

Real results from our real community member!

What would YOU do with an extra $500?

Start your journey: Link in bio

#SuccessStory #SavingsGoals #SusuMoney`,
      hashtags: '#Testimonial #Success #RealResults',
      bestTime: '6:00 PM'
    },
    {
      day: 'Wednesday',
      week: 3,
      type: 'educational',
      platform: 'all',
      title: 'Savings Tip',
      caption: `💡 SAVINGS TIP: The $1 a day challenge!

$1/day = $30/month = $365/year

Now imagine doing this with a community that holds you accountable! 🤝

That's the power of Susu! 💰

#SavingsTips #MoneyChallenge #FinancialLiteracy`,
      hashtags: '#MoneyTips #Challenge #Education',
      bestTime: '12:00 PM'
    },
    {
      day: 'Friday',
      week: 3,
      type: 'cta',
      platform: 'all',
      title: 'Weekend CTA',
      caption: `Weekend plans? 📅

✅ Join Susu Money
✅ Create your first savings club
✅ Invite your friends
✅ Start building wealth together!

It's FREE to start! Link in bio 👆

Happy saving! 💰🎉

#WeekendVibes #SusuMoney #StartSaving`,
      hashtags: '#Weekend #CTA #JoinNow',
      bestTime: '5:00 PM'
    }
  ];

  const socialAccounts = [
    {
      platform: 'Instagram',
      icon: InstagramIcon,
      username: '@globalsusumoneys',
      url: 'https://instagram.com/globalsusumoneys',
      color: 'bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF]',
      status: 'active'
    },
    {
      platform: 'TikTok',
      icon: TikTokIcon,
      username: '@susumoney',
      url: 'https://tiktok.com/@susumoney',
      color: 'bg-black',
      status: 'setup'
    },
    {
      platform: 'Facebook',
      icon: FacebookIcon,
      username: 'Susu Money',
      url: 'https://facebook.com/susumoney',
      color: 'bg-[#1877F2]',
      status: 'setup'
    },
    {
      platform: 'X (Twitter)',
      icon: XIcon,
      username: '@susumoney',
      url: 'https://x.com/susumoney',
      color: 'bg-black',
      status: 'setup'
    },
    {
      platform: 'YouTube',
      icon: YouTubeIcon,
      username: '@susumoney',
      url: 'https://youtube.com/@susumoney',
      color: 'bg-red-600',
      status: 'setup'
    }
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <section className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 text-white py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <Badge className="bg-white/20 text-white mb-4">
            <Sparkles className="w-4 h-4 mr-1" />
            Social Media Brand Kit
          </Badge>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            Social Media Hub
          </h1>
          <p className="text-white/90 max-w-2xl">
            Everything you need to launch and manage Susu Money's social media presence. 
            Usernames, bios, hashtags, and ready-to-post content!
          </p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <Tabs defaultValue="accounts" className="space-y-8">
          <TabsList className="bg-white border border-stone-200 p-1 rounded-xl">
            <TabsTrigger value="accounts" className="rounded-lg">📱 Accounts</TabsTrigger>
            <TabsTrigger value="brandkit" className="rounded-lg">🎨 Brand Kit</TabsTrigger>
            <TabsTrigger value="calendar" className="rounded-lg">📅 Content Calendar</TabsTrigger>
            <TabsTrigger value="hashtags" className="rounded-lg">#️⃣ Hashtags</TabsTrigger>
          </TabsList>

          {/* Accounts Tab */}
          <TabsContent value="accounts">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {socialAccounts.map((account) => (
                <Card key={account.platform} className="border-stone-200">
                  <CardContent className="p-4">
                    <div className="flex items-center mb-4">
                      <div className={`w-12 h-12 ${account.color} rounded-xl flex items-center justify-center text-white mr-3`}>
                        <account.icon />
                      </div>
                      <div>
                        <h3 className="font-semibold">{account.platform}</h3>
                        <p className="text-sm text-stone-600">{account.username}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 rounded-lg"
                        onClick={() => copyToClipboard(account.username, account.platform)}
                      >
                        {copiedId === account.platform ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                      <Button
                        size="sm"
                        className={`flex-1 rounded-lg ${account.color} text-white border-0`}
                        onClick={() => window.open(account.url, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Open
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {/* Link in Bio Card */}
              <Card className="border-2 border-dashed border-indigo-300 bg-indigo-50/50">
                <CardContent className="p-4">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white mr-3">
                      <LinkIcon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Link in Bio</h3>
                      <p className="text-sm text-stone-600">{baseUrl}/links</p>
                    </div>
                  </div>
                  <Button
                    className="w-full rounded-lg bg-indigo-600 hover:bg-indigo-700"
                    onClick={() => window.open('/links', '_blank')}
                  >
                    View Link Page
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Brand Kit Tab */}
          <TabsContent value="brandkit">
            <div className="space-y-6">
              {/* Usernames */}
              <Card className="border-stone-200">
                <CardHeader>
                  <CardTitle className="flex items-center text-lg">
                    <AtSign className="w-5 h-5 mr-2 text-indigo-600" />
                    Suggested Usernames
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Badge className="bg-indigo-100 text-indigo-700 text-base px-3 py-1">
                      {brandKit.usernames.primary}
                    </Badge>
                    {brandKit.usernames.alternatives.map((username) => (
                      <Badge key={username} variant="outline" className="text-base px-3 py-1">
                        {username}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-sm text-stone-600">
                    Primary username: <strong>{brandKit.usernames.primary}</strong> - Use this consistently across all platforms for brand recognition.
                  </p>
                </CardContent>
              </Card>

              {/* Bios */}
              <Card className="border-stone-200">
                <CardHeader>
                  <CardTitle className="flex items-center text-lg">
                    <FileText className="w-5 h-5 mr-2 text-indigo-600" />
                    Platform Bios
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {Object.entries(brandKit.bios).map(([platform, bio]) => (
                    <div key={platform} className="bg-stone-50 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold capitalize">{platform}</h4>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyToClipboard(bio, `bio-${platform}`)}
                          className="rounded-lg"
                        >
                          {copiedId === `bio-${platform}` ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          <span className="ml-1">Copy</span>
                        </Button>
                      </div>
                      <pre className="whitespace-pre-wrap text-sm text-stone-700 font-sans">
                        {bio}
                      </pre>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Content Calendar Tab */}
          <TabsContent value="calendar">
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">First 10 Posts - Ready to Publish!</h2>
                <Badge className="bg-green-100 text-green-700">
                  <Calendar className="w-4 h-4 mr-1" />
                  3 Weeks of Content
                </Badge>
              </div>
              
              {contentCalendar.map((post, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="border-stone-200 hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className="text-xs">
                              Week {post.week}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {post.day}
                            </Badge>
                            <Badge className={`text-xs ${
                              post.type === 'video' ? 'bg-red-100 text-red-700' :
                              post.type === 'carousel' ? 'bg-purple-100 text-purple-700' :
                              post.type === 'story' ? 'bg-pink-100 text-pink-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {post.type === 'video' && <Video className="w-3 h-3 mr-1" />}
                              {post.type === 'image' && <Image className="w-3 h-3 mr-1" />}
                              {post.type}
                            </Badge>
                            <span className="text-xs text-stone-500 flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              {post.bestTime}
                            </span>
                          </div>
                          <h3 className="font-semibold text-stone-900 mb-2">
                            #{index + 1}: {post.title}
                          </h3>
                          <div className="bg-stone-50 rounded-lg p-3 mb-2">
                            <pre className="whitespace-pre-wrap text-sm text-stone-700 font-sans">
                              {post.caption}
                            </pre>
                          </div>
                          <p className="text-xs text-stone-500">
                            Platform: <strong>{post.platform}</strong>
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="ml-4 rounded-lg"
                          onClick={() => copyToClipboard(post.caption, `post-${index}`)}
                        >
                          {copiedId === `post-${index}` ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </TabsContent>

          {/* Hashtags Tab */}
          <TabsContent value="hashtags">
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border-stone-200">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center">
                    <Hash className="w-5 h-5 mr-2 text-indigo-600" />
                    Brand Hashtags
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {brandKit.hashtags.primary.map((tag) => (
                      <Badge key={tag} className="bg-indigo-100 text-indigo-700">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full rounded-lg"
                    onClick={() => copyToClipboard(brandKit.hashtags.primary.join(' '), 'primary-tags')}
                  >
                    {copiedId === 'primary-tags' ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                    Copy All
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-stone-200">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center">
                    <TrendingUp className="w-5 h-5 mr-2 text-green-600" />
                    Trending Hashtags
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {brandKit.hashtags.trending.map((tag) => (
                      <Badge key={tag} className="bg-green-100 text-green-700">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full rounded-lg"
                    onClick={() => copyToClipboard(brandKit.hashtags.trending.join(' '), 'trending-tags')}
                  >
                    {copiedId === 'trending-tags' ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                    Copy All
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-stone-200">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center">
                    <Globe className="w-5 h-5 mr-2 text-amber-600" />
                    Niche Hashtags
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {brandKit.hashtags.niche.map((tag) => (
                      <Badge key={tag} className="bg-amber-100 text-amber-700">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full rounded-lg"
                    onClick={() => copyToClipboard(brandKit.hashtags.niche.join(' '), 'niche-tags')}
                  >
                    {copiedId === 'niche-tags' ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                    Copy All
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Full Hashtag Set */}
            <Card className="border-stone-200 mt-6">
              <CardHeader>
                <CardTitle className="text-lg">Complete Hashtag Set</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-stone-50 rounded-xl p-4 mb-4">
                  <code className="text-sm text-stone-700">
                    {[...brandKit.hashtags.primary, ...brandKit.hashtags.trending, ...brandKit.hashtags.niche].join(' ')}
                  </code>
                </div>
                <Button
                  variant="outline"
                  className="rounded-lg"
                  onClick={() => copyToClipboard(
                    [...brandKit.hashtags.primary, ...brandKit.hashtags.trending, ...brandKit.hashtags.niche].join(' '),
                    'all-tags'
                  )}
                >
                  {copiedId === 'all-tags' ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                  Copy All Hashtags
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
